import { Select as SelectPrimitive } from '@base-ui/react/select';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import {
  createContext,
  type KeyboardEvent,
  type ReactNode,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from '@/components/ui/input-group';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useTranslator } from '@/hooks/useTranslator';
import { cn } from '@/lib/utils/cn';
import { SearchContext, useAppSelectSearch } from './hooks/useAppSelectSearch';
import { useRegistry } from './hooks/useRegistry';

import type {
  AppSelectContentProps,
  AppSelectGroupProps,
  AppSelectItemProps,
  AppSelectProps,
  AppSelectRegistryCtx,
  AppSelectTriggerProps,
} from './types';

// ─── Mode context ─────────────────────────────────────────────────────────────

type SelectMode = 'single' | 'multi';
const AppSelectModeContext = createContext<SelectMode>('single');

// ─── Label registry (value → label) ──────────────────────────────────────────

const AppSelectRegistryContext = createContext<AppSelectRegistryCtx>({
  register: () => {},
  getLabel: () => undefined,
  getAll: () => new Map(),
});

// ─── Single-select contexts ───────────────────────────────────────────────────

const AppSelectValueContext = createContext('');
const AppSelectClearContext = createContext<(() => void) | null>(null);

// ─── Multi-select context ─────────────────────────────────────────────────────

interface MultiCtx {
  values: string[];
  toggle: (val: string) => void;
  clearAll: () => void;
  maxDisplay: number;
}

const AppSelectMultiContext = createContext<MultiCtx>({
  values: [],
  toggle: () => {},
  clearAll: () => {},
  maxDisplay: 5,
});

// ─── Error context ────────────────────────────────────────────────────────────

const AppSelectErrorContext = createContext<string | undefined>(undefined);

// ─── Search context (Content → Items) ────────────────────────────────────────
// `SearchContext` + `useAppSelectSearch` live in ./hooks/useAppSelectSearch so
// `SearchBridge` can consume them without importing this barrel (avoids a cycle).

// ─── AppSelect sub-roots ──────────────────────────────────────────────────────

function AppSelectMultiRoot({
  values,
  onValuesChange,
  maxDisplay = 5,
  error,
  labels,
  children,
}: Extract<AppSelectProps, { multiple: true }>) {
  const registry = useRegistry();
  // Eagerly register provided labels so the trigger renders chips correctly
  // on first paint, before the popup mounts its items.
  if (labels) {
    for (const [value, label] of Object.entries(labels)) {
      registry.register(value, label);
    }
  }
  const toggle = (val: string) =>
    onValuesChange(
      values.includes(val) ? values.filter(v => v !== val) : [...values, val],
    );
  return (
    <AppSelectModeContext.Provider value='multi'>
      <AppSelectRegistryContext.Provider value={registry}>
        <AppSelectMultiContext.Provider
          value={{
            values,
            toggle,
            clearAll: () => onValuesChange([]),
            maxDisplay,
          }}
        >
          <AppSelectErrorContext.Provider value={error}>
            <Popover>{children}</Popover>
          </AppSelectErrorContext.Provider>
        </AppSelectMultiContext.Provider>
      </AppSelectRegistryContext.Provider>
    </AppSelectModeContext.Provider>
  );
}

function AppSelectSingleRoot({
  value: valueProp,
  defaultValue,
  onValueChange,
  onBlur,
  disabled,
  name,
  required,
  error,
  open,
  onOpenChange,
  children,
}: Extract<AppSelectProps, { multiple?: false | undefined }>) {
  const registry = useRegistry();
  const [uncontrolledValue, setUncontrolledValue] = useState(
    defaultValue ?? '',
  );
  const isControlled = valueProp !== undefined;
  const currentValue = isControlled ? (valueProp ?? '') : uncontrolledValue;
  const handleClear = () => {
    if (!isControlled) setUncontrolledValue('');
    onValueChange?.(null);
  };
  const primitiveValue = valueProp === '' ? null : valueProp;
  return (
    <AppSelectModeContext.Provider value='single'>
      <AppSelectRegistryContext.Provider value={registry}>
        <AppSelectValueContext.Provider value={currentValue}>
          <AppSelectClearContext.Provider value={handleClear}>
            <AppSelectErrorContext.Provider value={error}>
              <SelectPrimitive.Root
                value={primitiveValue}
                defaultValue={defaultValue}
                disabled={disabled}
                name={name}
                required={required}
                {...(open !== undefined ? { open } : {})}
                {...(onOpenChange ? { onOpenChange } : {})}
                onValueChange={v => {
                  if (!isControlled) setUncontrolledValue(v ?? '');
                  onValueChange?.(v);
                  onBlur?.();
                }}
              >
                {children}
              </SelectPrimitive.Root>
            </AppSelectErrorContext.Provider>
          </AppSelectClearContext.Provider>
        </AppSelectValueContext.Provider>
      </AppSelectRegistryContext.Provider>
    </AppSelectModeContext.Provider>
  );
}

// ─── AppSelect (root) ─────────────────────────────────────────────────────────

function AppSelect(props: AppSelectProps) {
  if (props.multiple === true) return <AppSelectMultiRoot {...props} />;
  return <AppSelectSingleRoot {...props} />;
}

// ─── MultiTrigger (width-based chip overflow) ────────────────────────────────

const CHIP_GAP = 4; // gap-1
const MORE_W = 56; // approx width of "+99 more" at text-xs

interface MultiTriggerProps {
  disabled?: boolean;
  placeholder: string;
  heightClass: string;
  errorClasses: string;
  className?: string;
  renderValue?: ((value: string, label: string) => ReactNode) | undefined;
}

function MultiTrigger({
  disabled,
  placeholder,
  heightClass,
  errorClasses,
  className,
  renderValue,
}: MultiTriggerProps) {
  const { getTranslation: t } = useTranslator({ page: 'shared' });
  const { values, toggle, clearAll } = useContext(AppSelectMultiContext);
  const { getLabel } = useContext(AppSelectRegistryContext);

  const containerRef = useRef<HTMLSpanElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const [visibleCount, setVisibleCount] = useState(values.length);

  const selectedLabels = values.map(v => ({ val: v, label: getLabel(v) ?? v }));

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    function recalculate() {
      const containerW = container!.getBoundingClientRect().width;
      if (containerW === 0) return;
      const measure = measureRef.current;
      if (!measure) return;
      const chipEls = Array.from(
        measure.querySelectorAll<HTMLSpanElement>('[data-chip-m]'),
      );
      let used = 0;
      let count = 0;
      for (let i = 0; i < chipEls.length; i++) {
        const chipW = chipEls[i]!.getBoundingClientRect().width;
        const remaining = chipEls.length - i - 1;
        const badgeSpace = remaining > 0 ? CHIP_GAP + MORE_W : 0;
        if (used + chipW + badgeSpace <= containerW) {
          used += chipW + CHIP_GAP;
          count++;
        } else {
          break;
        }
      }
      setVisibleCount(Math.max(count, 1));
    }

    const ro = new ResizeObserver(recalculate);
    ro.observe(container);
    recalculate();
    return () => ro.disconnect();
  }, []);

  useLayoutEffect(() => {
    setVisibleCount(values.length);
  }, [values]);

  const visible = selectedLabels.slice(0, visibleCount);
  const overflow = selectedLabels.length - visibleCount;

  const triggerEl = (
    <button
      type='button'
      disabled={disabled}
      className={cn(
        'flex w-full items-center gap-1.5 rounded-md border border-input bg-input/20 dark:bg-input/30 px-3 text-xs font-normal transition-colors outline-none text-left',
        heightClass,
        'hover:border-ring focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/30',
        'data-popup-open:border-ring data-popup-open:ring-2 data-popup-open:ring-ring/30',
        'disabled:cursor-not-allowed disabled:opacity-50',
        errorClasses,
        { 'pl-0.5': values.length > 0 },
        className,
      )}
    >
      {values.length === 0 ? (
        <span className='flex-1 truncate text-muted-foreground'>
          {placeholder}
        </span>
      ) : (
        <span
          ref={containerRef}
          className='relative flex-1 min-w-0 overflow-hidden'
        >
          {/* hidden measure layer — all chips at natural width */}
          <span
            ref={measureRef}
            className='invisible absolute flex items-center gap-1 pointer-events-none whitespace-nowrap'
            aria-hidden
          >
            {selectedLabels.map(({ val, label }) => (
              <span
                key={val}
                data-chip-m
                className='inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-1 text-xs bg-muted text-foreground border border-border'
              >
                {renderValue ? renderValue(val, label) : label}
                <span className='size-2.5' />
              </span>
            ))}
          </span>

          {/* visible chips */}
          <span className='flex items-center gap-1'>
            {visible.map(({ val, label }) => (
              <span
                key={val}
                className='inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-1 text-xs bg-muted text-foreground border border-border'
              >
                {renderValue ? (
                  renderValue(val, label)
                ) : (
                  <span className='truncate max-w-36'>{label}</span>
                )}
                <button
                  type='button'
                  aria-label={`Remove ${label}`}
                  onClick={e => {
                    e.stopPropagation();
                    toggle(val);
                  }}
                  className='shrink-0 text-muted-foreground hover:text-foreground transition-colors cursor-pointer bg-transparent border-none p-0'
                >
                  <X className='size-2.5' />
                </button>
              </span>
            ))}
            {overflow > 0 && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <span className='self-center text-xs text-muted-foreground shrink-0 cursor-default' />
                    }
                  >
                    {t('more', { count: overflow })}
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className='flex flex-col gap-0.5'>
                      {selectedLabels.slice(visibleCount).map(({ label }) => (
                        <span key={label}>{label}</span>
                      ))}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </span>
        </span>
      )}
      {values.length > 0 ? (
        <button
          type='button'
          aria-label='Clear all'
          onClick={e => {
            e.stopPropagation();
            clearAll();
          }}
          className='ml-auto shrink-0 text-muted-foreground hover:text-foreground transition-colors cursor-pointer bg-transparent border-none p-0'
        >
          <X className='size-3.5' />
        </button>
      ) : (
        <ChevronDown className='ml-auto size-4 shrink-0 opacity-50' />
      )}
    </button>
  );

  return <PopoverTrigger render={triggerEl} />;
}

// ─── AppSelectTrigger ─────────────────────────────────────────────────────────

function AppSelectTrigger({
  placeholder,
  className,
  disabled,
  displayValue,
  size = 'default',
  clearable,
  renderValue,
}: AppSelectTriggerProps) {
  const { getTranslation: t } = useTranslator({ page: 'shared' });
  const mode = useContext(AppSelectModeContext);
  const error = useContext(AppSelectErrorContext);
  const registry = useContext(AppSelectRegistryContext);
  const singleValue = useContext(AppSelectValueContext);
  const onClear = useContext(AppSelectClearContext);
  const heightClass = size === 'xs' ? 'h-5' : size === 'sm' ? 'h-6' : 'h-8';
  const resolvedPlaceholder = placeholder ?? t('select');
  const errorClasses = error
    ? 'border-destructive ring-2 ring-destructive/20 dark:border-destructive/50 dark:ring-destructive/40'
    : '';

  if (mode === 'multi') {
    return (
      <MultiTrigger
        {...(disabled !== undefined ? { disabled } : {})}
        placeholder={resolvedPlaceholder}
        heightClass={heightClass}
        errorClasses={errorClasses}
        {...(className !== undefined ? { className } : {})}
        {...(renderValue !== undefined ? { renderValue } : {})}
      />
    );
  }

  // single mode
  const { getLabel } = registry;
  const currentValue = singleValue;
  const resolvedLabel =
    displayValue ?? (currentValue ? (getLabel(currentValue) ?? null) : null);
  const label = resolvedLabel;
  const showClear = clearable && !!label;

  return (
    <div className='relative' style={{ display: 'grid', width: '100%' }}>
      <SelectPrimitive.Trigger
        disabled={disabled}
        render={(props: React.ComponentProps<'button'>) => (
          <button {...props} style={{ ...props.style, minWidth: 0 }} />
        )}
        className={cn(
          'flex items-center justify-between gap-2 rounded-md border border-input bg-input/20 dark:bg-input/30 px-3 text-xs font-normal transition-colors outline-none',
          heightClass,
          'focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/30',
          'disabled:cursor-not-allowed disabled:opacity-50',
          'data-popup-open:border-ring data-popup-open:ring-2 data-popup-open:ring-ring/30',
          showClear && 'pr-8',
          errorClasses,
          className,
        )}
      >
        <span
          className={cn(
            'flex-1 text-left truncate flex items-center gap-1.5',
            !label && 'text-muted-foreground',
          )}
        >
          {label
            ? renderValue
              ? renderValue(currentValue, label)
              : label
            : resolvedPlaceholder}
        </span>
        {!showClear && (
          <SelectPrimitive.Icon>
            <ChevronDown className='size-4 shrink-0 opacity-50' aria-hidden />
          </SelectPrimitive.Icon>
        )}
      </SelectPrimitive.Trigger>
      {showClear && (
        <button
          type='button'
          aria-label='Clear'
          onClick={e => {
            e.stopPropagation();
            onClear?.();
          }}
          className='absolute right-2 top-1/2 -translate-y-1/2 shrink-0 rounded p-0.5 text-muted-foreground hover:text-foreground transition-colors'
        >
          <X className='size-3.5' aria-hidden />
        </button>
      )}
    </div>
  );
}

// ─── AppSelectContent ─────────────────────────────────────────────────────────

function AppSelectContent({
  searchPlaceholder,
  emptyText,
  className,
  children,
  showSearch = true,
  footer,
}: AppSelectContentProps) {
  const { getTranslation: t } = useTranslator({ page: 'shared' });
  const mode = useContext(AppSelectModeContext);
  const { getAll } = useContext(AppSelectRegistryContext);
  const resolvedSearchPlaceholder = searchPlaceholder ?? t('search');
  const resolvedEmptyText = emptyText ?? t('noOptions');
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const id = setTimeout(() => inputRef.current?.focus(), 30);
    return () => clearTimeout(id);
  }, []);

  const hasMatch =
    !search ||
    [...getAll().values()].some(label =>
      label.toLowerCase().includes(search.toLowerCase()),
    );

  const searchBox = showSearch && (
    <div className='p-1 pb-0 shrink-0'>
      <InputGroup>
        <InputGroupInput
          ref={inputRef}
          type='search'
          aria-label={resolvedSearchPlaceholder}
          autoComplete='off'
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={resolvedSearchPlaceholder}
          className='text-xs/relaxed'
        />
        <InputGroupAddon align='inline-end'>
          <Search className='size-3.5 shrink-0 opacity-50' />
        </InputGroupAddon>
      </InputGroup>
    </div>
  );

  const emptyState = showSearch && search && !hasMatch && (
    <p role='status' className='py-6 text-center text-xs text-muted-foreground'>
      {resolvedEmptyText}
    </p>
  );

  if (mode === 'multi') {
    return (
      <SearchContext.Provider value={search}>
        <PopoverContent
          align='start'
          sideOffset={4}
          className={cn(
            'w-(--anchor-width) p-0 flex flex-col overflow-hidden',
            className,
          )}
        >
          {searchBox}
          <div
            className={cn('overflow-y-auto p-1', !showSearch && 'pt-1')}
            style={{ maxHeight: 220 }}
          >
            {children}
            {emptyState}
          </div>
          {footer && (
            <div className='border-t border-border/50 p-1 shrink-0'>
              {footer}
            </div>
          )}
        </PopoverContent>
      </SearchContext.Provider>
    );
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key.length === 1 || e.key === 'Backspace' || e.key === 'Delete')
      e.stopPropagation();
    if (e.key === 'Escape' && search) {
      e.stopPropagation();
      setSearch('');
    }
    if (e.key === 'Tab') {
      e.preventDefault();
      e.stopPropagation();
      const highlighted = listRef.current?.querySelector('[data-highlighted]');
      if (highlighted instanceof HTMLElement) {
        highlighted.click();
      } else {
        listRef.current?.dispatchEvent(
          new KeyboardEvent('keydown', {
            key: 'ArrowDown',
            bubbles: true,
            cancelable: true,
          }),
        );
      }
    }
  }

  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Positioner
        sideOffset={4}
        align='start'
        alignItemWithTrigger={false}
        className='isolate z-300'
      >
        <SelectPrimitive.Popup
          className={cn(
            'flex flex-col w-(--anchor-width) max-h-(--available-height) min-h-24 rounded-lg bg-popover text-popover-foreground shadow-md ring-1 ring-foreground/10 outline-hidden overflow-hidden',
            'origin-(--transform-origin) duration-100',
            'data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95',
            'data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
            'data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2',
            className,
          )}
        >
          {showSearch && (
            <div className='p-1 pb-0 shrink-0'>
              <InputGroup>
                <InputGroupInput
                  ref={inputRef}
                  type='search'
                  aria-label={resolvedSearchPlaceholder}
                  aria-autocomplete='list'
                  autoComplete='off'
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={resolvedSearchPlaceholder}
                  className='text-xs/relaxed'
                />
                <InputGroupAddon align='inline-end'>
                  <Search className='size-3.5 shrink-0 opacity-50' />
                </InputGroupAddon>
              </InputGroup>
            </div>
          )}
          <SearchContext.Provider value={search}>
            <SelectPrimitive.List
              ref={listRef}
              onKeyDown={e => {
                if (e.key === 'Tab') {
                  e.preventDefault();
                  const h =
                    listRef.current?.querySelector('[data-highlighted]');
                  if (h instanceof HTMLElement) h.click();
                }
              }}
              className={cn(
                'flex-1 min-h-0 overflow-y-auto no-scrollbar px-1 pb-1',
                !showSearch && 'pt-1',
              )}
              style={{ maxHeight: 256 }}
            >
              {children}
              {emptyState}
            </SelectPrimitive.List>
            {footer && (
              <div className='border-t border-border/50 p-1 shrink-0'>
                {footer}
              </div>
            )}
          </SearchContext.Provider>
        </SelectPrimitive.Popup>
      </SelectPrimitive.Positioner>
    </SelectPrimitive.Portal>
  );
}

// ─── AppSelectGroup ───────────────────────────────────────────────────────────

function AppSelectGroup({ label, className, children }: AppSelectGroupProps) {
  const mode = useContext(AppSelectModeContext);
  const groupLabel = label && (
    <p className='sticky top-0 z-10 -mx-1 px-2.5 py-1.5 text-xs font-semibold uppercase tracking-wide text-foreground bg-popover border-b border-border/40 mb-0.5'>
      {label}
    </p>
  );
  if (mode === 'multi') {
    return (
      <div className={cn('scroll-my-1', className)}>
        {groupLabel}
        {children}
      </div>
    );
  }
  return (
    <SelectPrimitive.Group className={cn('scroll-my-1', className)}>
      {label && (
        <SelectPrimitive.GroupLabel className='sticky top-0 z-10 -mx-1 px-2.5 py-1.5 text-xs font-semibold uppercase tracking-wide text-foreground bg-popover border-b border-border/40 mb-0.5'>
          {label}
        </SelectPrimitive.GroupLabel>
      )}
      {children}
    </SelectPrimitive.Group>
  );
}

// ─── AppSelectItem ────────────────────────────────────────────────────────────

function AppSelectItem({
  value,
  disabled,
  className,
  label: labelProp,
  children,
}: AppSelectItemProps) {
  const mode = useContext(AppSelectModeContext);
  const search = useContext(SearchContext);
  const { register } = useContext(AppSelectRegistryContext);
  const { values, toggle } = useContext(AppSelectMultiContext);
  const label = labelProp ?? (typeof children === 'string' ? children : '');

  useLayoutEffect(() => {
    if (label) register(value, label);
  }, [value, label, register]);

  if (search && label && !label.toLowerCase().includes(search.toLowerCase()))
    return null;

  if (mode === 'multi') {
    const checked = values.includes(value);
    return (
      <button
        type='button'
        disabled={disabled}
        onClick={() => toggle(value)}
        className={cn(
          'flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-xs text-left transition-colors',
          'hover:bg-brand-accent/10',
          checked ? 'text-foreground' : 'text-muted-foreground',
          'disabled:pointer-events-none disabled:opacity-50',
          className,
        )}
      >
        <Checkbox
          checked={checked}
          onCheckedChange={() => toggle(value)}
          onClick={e => e.stopPropagation()}
          className='pointer-events-none size-3.5 shrink-0'
        />
        <span className='flex-1 flex items-center gap-1.5 min-w-0 overflow-hidden'>
          {children}
        </span>
      </button>
    );
  }

  return (
    <SelectPrimitive.Item
      value={value}
      disabled={disabled}
      className={cn(
        'relative flex min-h-8 w-full cursor-default items-center gap-2 rounded-md px-2.5 py-1 text-xs/relaxed outline-hidden select-none',
        'focus:bg-brand-accent/10',
        'data-highlighted:bg-brand-accent/10',
        'data-disabled:pointer-events-none data-disabled:opacity-50',
        className,
      )}
    >
      <SelectPrimitive.ItemText className='flex-1 whitespace-nowrap'>
        {children}
      </SelectPrimitive.ItemText>
      <SelectPrimitive.ItemIndicator
        render={
          <span className='pointer-events-none flex items-center justify-center' />
        }
      >
        <Check className='size-3.5 pointer-events-none' aria-hidden />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  );
}

// ─── AppSelectSeparator ───────────────────────────────────────────────────────

function AppSelectSeparator({ className }: { className?: string }) {
  const mode = useContext(AppSelectModeContext);
  if (mode === 'multi') {
    return (
      <div
        className={cn(
          'pointer-events-none -mx-1 my-1 h-px bg-border/50',
          className,
        )}
      />
    );
  }
  return (
    <SelectPrimitive.Separator
      className={cn(
        'pointer-events-none -mx-1 my-1 h-px bg-border/50',
        className,
      )}
    />
  );
}

// ─── Exports ──────────────────────────────────────────────────────────────────

export { SearchBridge } from './SearchBridge';
export type {
  AppSelectContentProps,
  AppSelectGroupProps,
  AppSelectItemProps,
  AppSelectProps,
  AppSelectTriggerProps,
} from './types';

export {
  AppSelect,
  AppSelectContent,
  AppSelectGroup,
  AppSelectItem,
  AppSelectSeparator,
  AppSelectTrigger,
  useAppSelectSearch,
};
